import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePlayerContext } from '../PlayerContext';
import { useStore } from '../hooks/useStore';
import { GearIcon, RoundInfoIcon, KeyboardIcon } from '../icons';
import { QualityMenu } from './QualityMenu';
import { LatencyToggle } from './LatencyToggle';
import { Toggle } from './Toggle';
import styles from './SettingsPanel.module.css';

type PanelView = 'main' | 'quality';

export function SettingsPanel() {
  const { store, eventBus } = usePlayerContext();
  const isDebugVisible = useStore(store, useCallback(s => s.isDebugVisible, []));
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<PanelView>('main');
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const mainPaneRef = useRef<HTMLDivElement>(null);
  const qualityPaneRef = useRef<HTMLDivElement>(null);

  // Set initial height synchronously before first paint — no transition on open
  useLayoutEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    const pane = mainPaneRef.current;
    if (!pane || !panel) return;
    panel.style.transition = 'none';
    panel.style.setProperty('--active-pane-height', `${pane.scrollHeight}px`);
  }, [isOpen]);

  // Animate height when navigating between views — re-enable transition first
  useEffect(() => {
    if (!isOpen) return;
    const pane = view === 'quality' ? qualityPaneRef.current : mainPaneRef.current;
    const panel = panelRef.current;
    if (!pane || !panel) return;
    panel.style.transition = '';
    panel.style.setProperty('--active-pane-height', `${pane.scrollHeight}px`);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to main view when panel closes
  useEffect(() => {
    if (!isOpen) setView('main');
  }, [isOpen]);

  // Emit settings:open when panel opens, settings:close when it closes
  useEffect(() => {
    if (!isOpen) return;
    eventBus.emit('settings:open');
    return () => { eventBus.emit('settings:close'); };
  }, [isOpen, eventBus]);

  // Close panel on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const root = rootRef.current;
      if (!root) return;
      const activeZoneRoot = root.closest('[data-active-zone-root="true"]');
      if (activeZoneRoot?.contains(target)) {
        return;
      }
      if (!root.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [isOpen]);

  const handleClosePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleTrigger = useCallback(() => {
    setIsOpen(o => !o);
  }, []);

  const handleToggleDebug = useCallback(() => {
    eventBus.emit('debug:toggle');
  }, [eventBus]);

  const handleOpenQuality = useCallback(() => {
    setView('quality');
  }, []);

  const handleBackFromQuality = useCallback(() => {
    setView('main');
  }, []);

  const handleOpenHotkeys = useCallback(() => {
    setIsOpen(false);
    eventBus.emit('hotkeys:open');
  }, [eventBus]);

  return (
    <div
      className={`${styles.settings}${isOpen ? ` ${styles.open}` : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={styles.triggerBtn}
        onClick={handleTrigger}
        data-active-zone-primary="true"
        aria-label="Настройки"
        aria-expanded={isOpen}
      >
        <span><GearIcon /></span>
      </button>

      <div className={styles.panel} ref={panelRef} data-active-zone-exclude="true">
        <div className={`${styles.viewTrack}${view === 'quality' ? ` ${styles.onQuality}` : ''}`}>
          {/* Pane 1 — main settings */}
          <div className={styles.viewPane} ref={mainPaneRef}>
            <div className={styles.rows}>
              {/* Quality row — navigates to quality submenu */}
              <QualityMenu mode="row" onOpenSubmenu={handleOpenQuality} />

              {/* Latency toggle row — hides itself when LL-HLS unavailable */}
              <LatencyToggle />

              {/* Debug console toggle row */}
              <div
                className={styles.row}
                role="button"
                tabIndex={0}
                onClick={handleToggleDebug}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggleDebug();
                  }
                }}
              >
                <span className={styles.icon}><RoundInfoIcon /></span>
                <span className={styles.label}><span className={styles.labelText}>Консоль</span></span>
                <Toggle checked={isDebugVisible} onChange={handleToggleDebug} />
              </div>

              {/* Hotkeys row — opens hotkeys overlay */}
              <div className={styles.row} onClick={handleOpenHotkeys} role="button" tabIndex={0} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleOpenHotkeys()}>
                <span className={styles.icon}><KeyboardIcon /></span>
                <span className={styles.label}><p className={styles.labelText}>Горячие клавиши</p></span>
                <span className={styles.iconSlot} />
              </div>
            </div>
          </div>

          {/* Pane 2 — quality submenu (always mounted for smooth slide) */}
          <div className={styles.viewPane} ref={qualityPaneRef}>
            <QualityMenu mode="submenu" onBack={handleBackFromQuality} onAction={handleClosePanel} />
          </div>
        </div>
      </div>
    </div>
  );
}
